import { useQuery } from "@tanstack/react-query";
import { http, createPublicClient } from "viem";
import { redstone } from "viem/chains";
import { useAppStore } from "../../../store/appStore";
import type { Blueprint } from "../useBlueprintQuery";
import { crossMatchBlueprint } from "./crossMatchBlueprint";
import { getObjectTypesAt } from "./getObjectTypeAt";

const viemPublicClient = createPublicClient({
  chain: redstone,
  transport: http(),
});

export function useTerrainObjectTypesQuery({
  blueprint,
}: {
  blueprint: Blueprint;
}) {
  const { terraformedObjects } = useAppStore();

  return useQuery({
    queryKey: ["terrain-object-types", blueprint, terraformedObjects],
    queryFn: async () => {
      const coords = blueprint.map(({ position }) => position);
      const placedObjectTypesAt = await getObjectTypesAt(
        viemPublicClient,
        coords,
      );

      const allPlacedObjects = placedObjectTypesAt;
      const totalObjects = blueprint.reduce(
        (acc, item) => {
          acc[item.objectTypeId] = (acc[item.objectTypeId] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      const { matchingObjectTypes, missingBlueprintObjectTypesAt } =
        crossMatchBlueprint(blueprint, allPlacedObjects);

      const placedObjects = matchingObjectTypes.reduce(
        (acc, item) => {
          acc[item.objectTypeId] = (acc[item.objectTypeId] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      return {
        totalObjects,
        placedObjects,
        missingBlueprintObjectTypesAt,
      };
    },
    refetchInterval: 2_000,
    refetchIntervalInBackground: true,
    staleTime: 0,
    gcTime: 0,
  });
}
